import {
  getTrips,
  getTrip,
  addTrip,
  updateTrip,
  deleteTrip,
  getSharedTrips
} from '../firebase/firestoreService';
import { linkRegistryConfigService } from './linkRegistryConfigService';

// Helper to detect if a string looks like a raw Firebase UID (not a human-readable name)
const looksLikeUid = (str) => str && str.length >= 20 && !str.includes('@') && !str.includes(' ');

export const tripService = {
  async fetchTrips(userId) {
    try {
      // 1. Fetch owned trips
      const ownedTripsPromise = getTrips(userId).then(trips =>
        trips.map(trip => ({
          ...trip,
          createdBy: trip.createdBy || userId // Backfill createdBy if missing
        }))
      );

      // 2. Fetch shared trip pointers
      const sharedPointersPromise = getSharedTrips(userId);

      const [ownedTrips, sharedPointers] = await Promise.all([
        ownedTripsPromise,
        sharedPointersPromise
      ]);

      // 3. Resolve shared trips
      const sharedTripsPromises = sharedPointers.map(async (pointer) => {
        try {
          const trip = await getTrip(pointer.ownerId, pointer.originalTripId);
          // Resolve display name with fallback: prefer displayName, then email, then friendly label
          const rawDisplayName = pointer.ownerDisplayName || pointer.ownerEmail || pointer.ownerId;
          const displayName = looksLikeUid(rawDisplayName) ? 'Trip Owner' : rawDisplayName;
          return {
            ...trip,
            isShared: true,
            sharedRole: pointer.role,
            sharedBy: pointer.ownerId,
            sharedByDisplayName: displayName,
            sharedByEmail: pointer.ownerEmail || '',
            sharedAt: pointer.sharedAt
          };
        } catch (error) {
          console.warn(`Could not fetch shared trip ${pointer.originalTripId}:`, error);
          return null; // Trip might have been deleted or access revoked
        }
      });

      const resolvedSharedTrips = (await Promise.all(sharedTripsPromises))
        .filter(trip => trip !== null);

      // 4. Merge and sort
      const allTrips = [...ownedTrips, ...resolvedSharedTrips];

      return allTrips.sort((a, b) => {
        // Sort by creation date descending (newest first)
        // Note: Shared trips might compare createdAt or sharedAt depending on preference
        // optimizing for "Recent activity" usually means createdAt or updatedAt
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
    } catch (error) {
      console.error('Error fetching trips:', error);
      throw error;
    }
  },

  async fetchTrip(userId, tripId) {
    try {
      return await getTrip(userId, tripId);
    } catch (error) {
      console.error('Error fetching trip:', error);
      throw error;
    }
  },

  async createTrip(userId, tripData) {
    try {
      // Ensure required fields are present
      const newTripData = {
        name: tripData.name,
        startDate: tripData.startDate || null,
        endDate: tripData.endDate || null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: userId
      };

      const tripId = await addTrip(userId, newTripData);
      await linkRegistryConfigService.bootstrapNewTripRegistry(userId, tripId);
      return tripId;
    } catch (error) {
      console.error('Error creating trip:', error);
      throw error;
    }
  },

  async updateTrip(userId, tripId, tripData) {
    try {
      const updateData = {
        ...tripData,
        updatedAt: new Date().toISOString()
      };

      await updateTrip(userId, tripId, updateData);
      return await this.fetchTrip(userId, tripId);
    } catch (error) {
      console.error('Error updating trip:', error);
      throw error;
    }
  },

  async deleteTrip(userId, tripId) {
    try {
      await deleteTrip(userId, tripId);
    } catch (error) {
      console.error('Error deleting trip:', error);
      throw error;
    }
  },

  async updateTripDates(userId, tripId, startDate, endDate) {
    try {
      const updateData = {
        startDate: startDate,
        endDate: endDate,
        updatedAt: new Date().toISOString()
      };

      await updateTrip(userId, tripId, updateData);
      return await this.fetchTrip(userId, tripId);
    } catch (error) {
      console.error('Error updating trip dates:', error);
      throw error;
    }
  },

  validateTripData(tripData) {
    const errors = [];

    if (!tripData.name || tripData.name.trim().length === 0) {
      errors.push('Trip name is required');
    }

    if (tripData.startDate && tripData.endDate) {
      const start = new Date(tripData.startDate);
      const end = new Date(tripData.endDate);

      if (end < start) {
        errors.push('End date cannot be before start date');
      }

      const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
      if (daysDiff > 60) {
        errors.push('Trip cannot exceed 60 days');
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  },

  formatTripDates(trip) {
    if (!trip) return null;

    return {
      ...trip,
      displayDates: trip.startDate && trip.endDate
        ? `${new Date(trip.startDate).toLocaleDateString()} - ${new Date(trip.endDate).toLocaleDateString()}`
        : 'No dates set'
    };
  }
};